import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  AdminChatConversationResponse,
  AdminChatInboxEntry,
  AdminChatInboxResponse,
  AdminChatMessageView,
  AdminChatOnlineRosterResponse,
} from '@kopechka/contracts';
import {
  getAdminChatInbox,
  getAdminChatConversation,
  getAdminChatOnline,
  listAdminUsers,
  markAdminChatRead,
  sendAdminChatMessage,
  type AdminUserListResponse,
} from '../api/endpoints.js';
import { resolveBaseUrl } from '../api/client.js';
import { useApiQuery, useDebouncedValue } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Modal,
  PageHead,
  Textarea,
} from '../ui/index.js';

const POLL_MS = 5000;

/**
 * `/messages` — admin↔member support inbox (D-231). One screen of every member
 * who has a conversation (left), the selected thread + reply composer (right).
 * Reads come from the chat projection read-models; presence (online / last
 * active) is enriched from Redis liveness. Gated on `chat:view`; reply needs
 * `chat:send`.
 */
export function ChatInboxPage(): JSX.Element {
  const canSend = useCan('chat:send');
  const inbox = useApiQuery<AdminChatInboxResponse>((o) => getAdminChatInbox(o), []);
  const [searchParams] = useSearchParams();
  // Deep-link from the user-inspect page: /messages?actor=<actorId> preselects.
  const [selected, setSelected] = useState<string | null>(
    () => searchParams.get('actor'),
  );
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeOnlineOnly, setComposeOnlineOnly] = useState(false);

  // Active-now count — cheap ZCOUNT poll for the "kaç kişi online" header badge.
  const online = useApiQuery<AdminChatOnlineRosterResponse>(
    (o) => getAdminChatOnline(o, false),
    [],
  );
  const reloadOnline = online.reload;

  // Poll the inbox list + the online count every 5s.
  const reloadInbox = inbox.reload;
  useEffect(() => {
    const t = window.setInterval(() => {
      reloadInbox();
      reloadOnline();
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [reloadInbox, reloadOnline]);

  const conversations: readonly AdminChatInboxEntry[] =
    inbox.kind === 'ok' ? inbox.data.conversations : [];
  const onlineCount = online.kind === 'ok' ? online.data.onlineCount : null;

  function openCompose(onlineOnly: boolean): void {
    setComposeOnlineOnly(onlineOnly);
    setComposeOpen(true);
  }
  function pickUser(actorId: string): void {
    setSelected(actorId);
    setComposeOpen(false);
  }

  return (
    <div>
      <PageHead
        crumb={<>Müşteri <Icon.ChevronRight size={11} /> Mesajlar</>}
        title="Mesajlar"
        subtitle="Üyelerle birebir destek sohbeti. Bir üyeye siz de yazabilirsiniz."
        actions={
          canSend ? (
            <div className="kop-chat-actions" style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                className="kop-online-pill"
                onClick={() => openCompose(true)}
                title="Çevrim içi üyeleri gör ve yaz"
              >
                <span className="kop-presence-dot kop-presence-dot--on" aria-hidden="true" />
                Çevrim içi: <strong>{onlineCount ?? '—'}</strong>
              </button>
              <Button variant="primary" icon={<Icon.Mail size={14} />} onClick={() => openCompose(false)}>
                Yeni mesaj
              </Button>
            </div>
          ) : null
        }
      />
      {composeOpen && (
        <ComposePicker
          onlineOnly={composeOnlineOnly}
          onPick={pickUser}
          onClose={() => setComposeOpen(false)}
        />
      )}
      <div className="kop-admin-chat" data-view={selected ? 'thread' : 'list'}>
        <Card>
          <CardBody>
            {inbox.kind === 'loading' && conversations.length === 0 ? (
              <LoadingState label="Konuşmalar yükleniyor…" />
            ) : inbox.kind === 'error' ? (
              <ErrorState reason={inbox.reason} onRetry={() => inbox.reload()} />
            ) : conversations.length === 0 ? (
              <EmptyState label="Henüz mesaj yok" hint="Bir üye yazdığında burada görünecek." />
            ) : (
              <ul className="kop-admin-chat-list">
                {conversations.map((c) => (
                  <li key={c.conversationId}>
                    <button
                      type="button"
                      className={
                        selected === c.conversationId
                          ? 'kop-admin-chat-item kop-admin-chat-item--active'
                          : 'kop-admin-chat-item'
                      }
                      onClick={() => setSelected(c.conversationId)}
                    >
                      <span className="kop-admin-chat-item-top">
                        <span className="kop-admin-chat-item-name">
                          <PresenceDot online={c.presence.isOnline} />
                          {c.customerEmail ?? c.customerActorId}
                        </span>
                        {c.unreadForAdmin > 0 && (
                          <Badge tone="danger">{c.unreadForAdmin}</Badge>
                        )}
                      </span>
                      <span className="kop-admin-chat-item-preview">
                        {c.lastSenderRole === 'admin' ? 'Siz: ' : ''}
                        {c.lastMessagePreview ?? '—'}
                      </span>
                      <span className="kop-admin-chat-item-meta">
                        {relativeTime(c.lastMessageAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            {selected ? (
              <ConversationPane
                key={selected}
                actorId={selected}
                canSend={canSend}
                onSent={() => inbox.reload()}
                onReadCleared={() => inbox.reload()}
                onBack={() => setSelected(null)}
              />
            ) : (
              <EmptyState label="Bir konuşma seçin" hint="Soldaki listeden bir üye seçin." />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

/**
 * Compose / online-roster picker. Search any member (live) or filter to who's
 * online now, then pick one to start (or continue) a conversation. Reuses
 * listAdminUsers (search + isOnlineNow + presence) — one modal for both the
 * "Yeni mesaj" and "Çevrim içi" entry points.
 */
function ComposePicker({
  onlineOnly,
  onPick,
  onClose,
}: {
  readonly onlineOnly: boolean;
  readonly onPick: (actorId: string) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const [onlyOnline, setOnlyOnline] = useState(onlineOnly);
  const search = useDebouncedValue(draft, 300);
  const q = useApiQuery<AdminUserListResponse>(
    (o) =>
      listAdminUsers(o, {
        page: 1,
        limit: 30,
        search,
        status: 'all',
        role: 'user',
        sortBy: onlyOnline ? 'isOnlineNow' : 'createdAt',
        sortOrder: 'desc',
      }),
    [search, onlyOnline],
  );
  const rows = q.kind === 'ok' ? q.data.data.users : [];
  const filtered = onlyOnline ? rows.filter((u) => u.isOnlineNow) : rows;
  return (
    <Modal open onClose={onClose} title="Üye seç ve yaz">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 'min(480px, 80vw)' }}>
        <Input
          type="search"
          placeholder="E-posta / kullanıcı adı ara…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          iconLeft={<Icon.Search size={14} />}
          autoFocus
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={onlyOnline}
            onChange={(e) => setOnlyOnline(e.target.checked)}
          />
          Sadece çevrim içi
        </label>
        <div className="kop-compose-list">
          {q.kind === 'loading' ? (
            <LoadingState label="Yükleniyor…" />
          ) : filtered.length === 0 ? (
            <EmptyState label="Eşleşen üye yok." />
          ) : (
            filtered.map((u) => (
              <button
                key={u.actorId}
                type="button"
                className="kop-compose-row"
                onClick={() => onPick(u.actorId)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <PresenceDot online={u.isOnlineNow} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email ?? u.username ?? u.actorId}
                  </span>
                </span>
                <span style={{ fontSize: 11, opacity: 0.6, flexShrink: 0 }}>
                  {u.isOnlineNow ? 'çevrim içi' : relativeTime(u.lastActiveAt)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

function ConversationPane({
  actorId,
  canSend,
  onSent,
  onReadCleared,
  onBack,
}: {
  readonly actorId: string;
  readonly canSend: boolean;
  readonly onSent: () => void;
  readonly onReadCleared: () => void;
  readonly onBack: () => void;
}): JSX.Element {
  const [data, setData] = useState<AdminChatConversationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // Optimistically-shown messages the admin just sent, before the projection
  // read-model catches up (the projection lags a few seconds). Reconciled on
  // each load() — dropped once the authoritative read-model contains them.
  const [pending, setPending] = useState<AdminChatMessageView[]>([]);
  const endRef = useRef<HTMLDivElement | null>(null);
  const markedRef = useRef(false);
  // Synchronous in-flight guard — blocks a double Enter/click before the
  // `sending` state re-render disables the button (compose first-send safety).
  const inFlightRef = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    const res = await getAdminChatConversation({ baseUrl: resolveBaseUrl() }, actorId);
    if (res.kind === 'ok') {
      setData(res.data);
      setError(null);
      // Drop optimistic messages the read-model now contains.
      const ids = new Set(res.data.messages.map((m) => m.id));
      setPending((prev) => prev.filter((m) => !ids.has(m.id)));
      // Clear the admin unread badge once per opened thread.
      if (!markedRef.current) {
        markedRef.current = true;
        await markAdminChatRead({ baseUrl: resolveBaseUrl() }, actorId);
        onReadCleared();
      }
    } else if (res.kind === 'forbidden') {
      setError('forbidden');
    } else if (res.kind !== 'auth_required') {
      setError('reason' in res ? res.reason : 'error');
    }
    setLoading(false);
  }, [actorId, onReadCleared]);

  // Initial load + 5s live refresh of the open thread.
  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [data?.messages.length, pending.length]);

  async function send(): Promise<void> {
    const body = draft.trim();
    if (!body || sending || inFlightRef.current) return;
    inFlightRef.current = true;
    setSending(true);
    try {
      const res = await sendAdminChatMessage({ baseUrl: resolveBaseUrl() }, actorId, body);
      if (res.kind === 'ok') {
        setDraft('');
        setSendError(null);
        // Show it instantly; load() reconciles when the projection catches up.
        setPending((prev) => [...prev, res.data.message]);
        await load();
        onSent();
      } else if (res.kind === 'forbidden') {
        setSendError('Yetki yok (chat:send).');
      } else {
        setSendError('reason' in res ? res.reason : 'Gönderilemedi.');
      }
    } finally {
      inFlightRef.current = false;
      setSending(false);
    }
  }

  if (loading && !data) return <LoadingState label="Konuşma yükleniyor…" />;
  if (error === 'forbidden') return <ErrorState reason="Yetki yok (chat:view)." />;
  if (error && !data) return <ErrorState reason={error} onRetry={() => void load()} />;

  return (
    <div className="kop-admin-chat-pane">
      <header className="kop-admin-chat-pane-head">
        <button
          type="button"
          className="kop-admin-chat-back"
          aria-label="Konuşma listesine dön"
          onClick={onBack}
        >
          ← Geri
        </button>
        <span className="kop-admin-chat-pane-name">
          <PresenceDot online={data?.presence.isOnline ?? false} />
          {data?.customerEmail ?? actorId}
        </span>
        <span className="kop-admin-chat-pane-presence">
          {data?.presence.isOnline
            ? 'Çevrimiçi'
            : `Son aktif: ${relativeTime(data?.presence.lastActiveAt ?? null)}`}
        </span>
      </header>

      <div className="kop-admin-chat-messages" role="log" aria-live="polite">
        {(() => {
          const base = data?.messages ?? [];
          const baseIds = new Set(base.map((m) => m.id));
          const merged = [...base, ...pending.filter((m) => !baseIds.has(m.id))];
          if (merged.length === 0) {
            return <p className="kop-admin-chat-empty">Bu konuşmada henüz mesaj yok.</p>;
          }
          return merged.map((m) => (
            <div
              key={m.id}
              className={
                m.senderRole === 'admin'
                  ? 'kop-admin-chat-msg kop-admin-chat-msg--admin'
                  : 'kop-admin-chat-msg kop-admin-chat-msg--customer'
              }
            >
              <span className="kop-admin-chat-bubble">{m.body}</span>
              <time className="kop-admin-chat-time" dateTime={m.createdAt}>
                {formatTime(m.createdAt)}
              </time>
            </div>
          ));
        })()}
        <div ref={endRef} />
      </div>

      {sendError && (
        <div style={{ padding: '0 4px 6px' }}>
          <Banner tone="danger">{sendError}</Banner>
        </div>
      )}
      <div className="kop-admin-chat-composer">
        <Textarea
          rows={2}
          value={draft}
          placeholder={canSend ? 'Yanıt yazın…' : 'Yanıtlama yetkiniz yok (chat:send).'}
          disabled={!canSend || sending}
          maxLength={4000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <Button
          variant="primary"
          loading={sending}
          disabled={!canSend || draft.trim().length === 0}
          onClick={() => void send()}
        >
          Gönder
        </Button>
      </div>
    </div>
  );
}

function PresenceDot({ online }: { readonly online: boolean }): JSX.Element {
  return (
    <span
      className={online ? 'kop-presence-dot kop-presence-dot--on' : 'kop-presence-dot'}
      aria-hidden="true"
    />
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Compact Turkish relative time for the inbox + presence line. */
export function relativeTime(iso: string | null): string {
  if (!iso) return 'hiç';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'hiç';
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return d.toLocaleDateString();
}
